import React, { useMemo } from 'react';
import ReactMarkdown, { Components, Options } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, {
  defaultSchema,
  Options as SanitizeSchema,
} from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

function CodeBlock({
  language,
  children,
}: {
  language: string | undefined;
  children: string;
}) {
  const displayLang = language || 'text';

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden my-3 text-[13px]">
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200">
        <span className="text-xs text-gray-500 font-mono">{displayLang}</span>
      </div>
      <SyntaxHighlighter
        style={oneLight}
        language={language || 'text'}
        PreTag="div"
        customStyle={{
          margin: 0,
          padding: '12px 16px',
          background: '#FAFAFA',
          fontSize: '13px',
          lineHeight: '1.6',
        }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
}

const remarkPlugins = [remarkGfm];

// rehype-raw lets the renderer pass through raw HTML embedded in markdown
// (e.g. the `<u>` tag emitted by the editor's Underline mark, which has no
// markdown equivalent). Message bodies can originate from outside Commenda —
// an inbound email reply is stored verbatim — so rehype-sanitize must follow it:
// without it, `<svg><script>` and `<iframe srcdoc>` both execute.
// `u` is the editor's Underline mark; the `ref:` href scheme is how callers encode a reference
// for their own `a` override to resolve (see `urlTransform`), and is never navigated to.
const sanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'u'],
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), 'ref'],
  },
};
const rehypePlugins: Options['rehypePlugins'] = [
  rehypeRaw,
  [rehypeSanitize, sanitizeSchema],
];

/**
 * Walks the rendered children tree and wraps regex matches in string text nodes
 * with a `<mark>` element. Block-level component overrides call this on their
 * children; the recursion handles inline marks (strong/em/code/a) automatically
 * so we don't have to wrap those too.
 */
function highlightTextNodes(
  children: React.ReactNode,
  regex: RegExp
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      const parts = child.split(regex);
      return parts.map((part, i) => {
        // Capture group means odd indices are matches, even are surrounding text.
        if (i % 2 === 1) {
          return (
            <mark key={`hl-${i}`} className="bg-yellow-200 text-text-primary">
              {part}
            </mark>
          );
        }
        return <React.Fragment key={`txt-${i}`}>{part}</React.Fragment>;
      });
    }
    if (
      React.isValidElement<{ children?: React.ReactNode }>(child) &&
      child.props.children !== undefined
    ) {
      return React.cloneElement(child, {
        children: highlightTextNodes(child.props.children, regex),
      });
    }
    return child;
  });
}

function buildComponents(searchQuery?: string | null): Components {
  const query = (searchQuery || '').trim();
  const regex =
    query.length >= 2
      ? new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      : null;

  const wrap: (children: React.ReactNode) => React.ReactNode = regex
    ? (children) => highlightTextNodes(children, regex)
    : (children) => children;

  return {
    p: ({ children }) => (
      <p className="my-2 leading-relaxed">{wrap(children)}</p>
    ),
    h1: ({ children }) => (
      <h1 className="text-xl font-semibold text-gray-900 mt-5 mb-2">
        {wrap(children)}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-lg font-semibold text-gray-900 mt-4 mb-2">
        {wrap(children)}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-base font-semibold text-gray-900 mt-3 mb-1.5">
        {wrap(children)}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-sm font-semibold text-gray-900 mt-3 mb-1">
        {wrap(children)}
      </h4>
    ),
    ul: ({ children }) => (
      <ul className="list-disc pl-6 my-2 space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-6 my-2 space-y-1">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{wrap(children)}</li>,
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {children}
      </a>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-gray-300 pl-4 my-3 text-gray-600 italic">
        {wrap(children)}
      </blockquote>
    ),
    code: ({ className, children, ...rest }) => {
      const match = /language-(\w+)/.exec(className || '');
      const text = String(children).replace(/\n$/, '');

      const isBlock =
        match ||
        text.includes('\n') ||
        (rest.node?.position &&
          rest.node.position.start.line !== rest.node.position.end.line);

      if (isBlock) {
        return <CodeBlock language={match?.[1]}>{text}</CodeBlock>;
      }

      return (
        // break-words so a token like `human.confirm` wraps instead of widening the bubble. In a
        // narrow column the message list's overflow-y makes the browser add a horizontal bar for
        // anything that overflows, so an unbreakable token costs the whole panel a scrollbar.
        <code className="px-1.5 py-0.5 bg-gray-100 text-gray-800 rounded text-[0.9em] font-mono break-words">
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className="mb-4 p-4 bg-[#0d1117] text-[#e6edf3] rounded-lg overflow-x-auto text-sm font-mono">
        {children}
      </pre>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto my-3">
        <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    tbody: ({ children }) => (
      <tbody className="divide-y divide-gray-200">{children}</tbody>
    ),
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => (
      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide border-b border-gray-200">
        {wrap(children)}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-3 py-2 text-gray-700 border-b border-gray-100">
        {wrap(children)}
      </td>
    ),
    hr: () => <hr className="my-4 border-gray-200" />,
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    em: ({ children }) => <em>{children}</em>,
    u: ({ children }) => <u className="underline">{children}</u>,
  };
}

interface MarkdownRendererProps {
  content: string;
  /** Optional search query — when 2+ chars, matches are highlighted in `<mark>`. */
  searchQuery?: string | null;
  /** Per-tag overrides layered on top of the defaults — e.g. a caller-specific `a` renderer. */
  componentOverrides?: Components;
  /**
   * Overrides react-markdown's link URL sanitizer. The default strips any
   * non-standard protocol (http/https/mailto/tel/relative only), so callers
   * that encode custom href schemes (e.g. `ref:ID`) must widen it here or the
   * href is blanked before their `a` override can read it.
   */
  urlTransform?: (url: string) => string;
}

export function MarkdownRenderer({
  content,
  searchQuery,
  componentOverrides,
  urlTransform,
}: MarkdownRendererProps) {
  const memoizedRemarkPlugins = useMemo(() => remarkPlugins, []);
  const memoizedRehypePlugins = useMemo(() => rehypePlugins, []);
  const components = useMemo(
    () => ({ ...buildComponents(searchQuery), ...componentOverrides }),
    [searchQuery, componentOverrides]
  );

  return (
    <ReactMarkdown
      remarkPlugins={memoizedRemarkPlugins}
      rehypePlugins={memoizedRehypePlugins}
      components={components}
      urlTransform={urlTransform}
    >
      {content}
    </ReactMarkdown>
  );
}
